import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { sendGetClientRequest, sendPatchClientRequest, sendPostClientRequest } from '../../../api/helper';

export const getMenu = createAsyncThunk('menu/getMenu', async () => {
  const response = await sendGetClientRequest(`/api/software-services`);
  return response.data;
});

export const getDetail = createAsyncThunk('menu/getDetail', async (parameters) => {
  const response = await sendGetClientRequest(
    parameters.group_plans
      ? `/api/software-services/${parameters.service}?group_plans=${parameters.group_plans}`
      : `/api/software-services/${parameters.service}`
  );
  return response.data;
});

// 服務管理列表用：清單 API（index）已一併帶回負責人與使用統計，因此單次呼叫即可，
// 不再逐一取 detail（原本的前端 N+1 已移除）。detail 只在點編輯開 modal 時才取。
export const getServices = createAsyncThunk('menu/getServices', async () => {
  const response = await sendGetClientRequest(`/api/software-services`);
  return response?.data ?? [];
});

export const getEnterpriseSetting = createAsyncThunk('menu/getEnterpriseSetting', async (parameters) => {
  const response = await sendGetClientRequest(`/api/enterprise_setting`);
  return response.data;
});

// 設定服務負責人
export const postRepresentativeUser = createAsyncThunk('menu/postRepresentativeUser', async (parameters) => {
  const response = await sendPostClientRequest(`/api/software-services/${parameters.service}/representative?user_email=${parameters.user_email}`);
  return response;
});

export const updateDetail = createAsyncThunk('menu/updateDetail', async (parameters) => {
  const postObj = {
    activate_mode: parameters.activate_mode,
    status: parameters.status,
  };
  const response = await sendPatchClientRequest(`/api/software-services/${parameters.service}`, postObj);
  return response;
});

export const updateDetailSetting = createAsyncThunk('menu/updateDetailSetting', async (parameters) => {
  const postObj = {
    activate_mode: parameters.activate_mode,
    status: parameters.status,
  };
  const response = await sendPatchClientRequest(`/api/software-services/${parameters.service}`, postObj);
  return response;
});

// 只更新「需 AI 席位」旗標；後端 validator 為 sometimes，僅送 need_ai_seat 不會動到 status/開通設定。
export const updateNeedAiSeat = createAsyncThunk('menu/updateNeedAiSeat', async (parameters) => {
  const postObj = {
    need_ai_seat: parameters.need_ai_seat,
  };
  const response = await sendPatchClientRequest(`/api/software-services/${parameters.service}`, postObj);
  return response;
});

// 更新服務說明（字串陣列）；送 null 代表清空。後端 validator 為 sometimes|nullable|array。
export const updateDescript = createAsyncThunk('menu/updateDescript', async (parameters) => {
  const postObj = {
    descript: parameters.descript,
  };
  const response = await sendPatchClientRequest(`/api/software-services/${parameters.service}`, postObj);
  return response;
});

export const updateEnterpriseSetting = createAsyncThunk('menu/updateEnterpriseSetting', async (parameters) => {
  const postObj = {
    enterprise_register_email_verify: parameters.enterprise_register_email_verify,
  };
  const response = await sendPostClientRequest(`/api/enterprise_setting`, postObj);
  return response;
});

const menuSlice = createSlice({
  name: 'menu',
  initialState: {
    menu: [],
    menuNew: [],
    services: [],
    detail: null,
    detailServiceId: null,
    loading: false,
    updateLoading: false,
    success: false,
    postError: false,
    postErrorMsg: '',
    error: true,
  },
  reducers: {
    removeSuccess: (state) => {
      state.success = false;
    },
    removePostError: (state) => {
      state.postError = false;
      state.postErrorMsg = '';
    },
  },
  extraReducers: {
    // GET MENU
    [getMenu.pending]: (state) => {
      state.loading = true;
    },
    [getMenu.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.menu = payload;
        state.loading = false;
      }
    },
    [getMenu.rejected]: (state) => {
      state.loading = false;
    },

    // GET SERVICES (服務管理列表，單次呼叫)
    [getServices.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.services = payload;
      }
    },

    // GET DETAIL
    [getDetail.pending]: (state) => {
      state.loading = true;
    },
    [getDetail.fulfilled]: (state, { payload, meta }) => {
      if (payload) {
        state.detail = payload;
        // 記錄這筆 detail 屬於哪個 service，切換 service 時才能判斷是否為前一個 service 的殘留資料。
        state.detailServiceId = meta?.arg?.service ?? null;
        state.loading = false;
      }
    },
    [getDetail.rejected]: (state) => {
      state.loading = false;
    },

    [getEnterpriseSetting.pending]: (state) => {
      state.loading = true;
    },
    [getEnterpriseSetting.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.detail = payload;
        // 企業帳號設定固定對應 serviceId '0'。
        state.detailServiceId = '0';
        state.loading = false;
      }
    },
    [getEnterpriseSetting.rejected]: (state) => {
      state.loading = false;
    },

    // SET REPRESENT USER
    [postRepresentativeUser.pending]: (state) => {
      state.updateLoading = true;
    },
    [postRepresentativeUser.fulfilled]: (state, { payload }) => {
      if (payload.msg === 'success') {
        state.updateLoading = false;
        state.success = true;
      }
    },
    [postRepresentativeUser.rejected]: (state) => {
      state.updateLoading = false;
    },

    // UPDATE DETAIL
    [updateDetail.pending]: (state) => {
      state.loading = true;
    },
    [updateDetail.fulfilled]: (state, { payload }) => {
      if (payload?.msg === 'success') {
        state.success = true;
      } else {
        state.postError = true;
        // 保留後端實際錯誤訊息（例如 "Cannot enable the service!" / "Cannot disable the service!"），
        // 讓畫面能顯示對應的正確原因，而非一律顯示「租賃中」。
        state.postErrorMsg = payload?.msg || '';
      }
      state.loading = false;
    },
    [updateDetail.rejected]: (state) => {
      state.loading = false;
    },

    // UPDATE DETAIL SETTING
    [updateDetailSetting.pending]: (state) => {
      state.loading = true;
    },
    [updateDetailSetting.fulfilled]: (state, { payload }) => {
      if (payload.msg === 'success') {
        state.loading = false;
      }
    },
    [updateDetailSetting.rejected]: (state) => {
      state.loading = false;
    },

    // UPDATE NEED AI SEAT
    [updateNeedAiSeat.pending]: (state) => {
      state.loading = true;
    },
    [updateNeedAiSeat.fulfilled]: (state, { payload }) => {
      if (payload.msg === 'success') {
        state.loading = false;
      }
    },
    [updateNeedAiSeat.rejected]: (state) => {
      state.loading = false;
    },

    // UPDATE DESCRIPT（成功回饋改用按鈕旁的行內藍字提示，不觸發全域 Toast/refetch）
    [updateDescript.pending]: (state) => {
      state.loading = true;
    },
    [updateDescript.fulfilled]: (state, { payload }) => {
      if (payload?.msg === 'success') {
        state.loading = false;
      }
    },
    [updateDescript.rejected]: (state) => {
      state.loading = false;
    },

    // UPDATE ENTERPRISE SETTING
    [updateEnterpriseSetting.pending]: (state) => {
      state.loading = true;
    },
    [updateEnterpriseSetting.fulfilled]: (state, { payload }) => {
      if (payload.msg === 'success') {
        state.loading = false;
      }
    },
    [updateEnterpriseSetting.rejected]: (state) => {
      state.loading = false;
    },
  },
});

export default menuSlice.reducer;
export const selectMenu = (state) => state.menu;
export const { removeSuccess, removePostError } = menuSlice.actions;
